'use client';

import posthog from 'posthog-js';
import { PostHogProvider as PHProvider, usePostHog } from 'posthog-js/react';
import { useEffect, type ReactNode } from 'react';
import { useSession } from '@/lib/auth-client';
import { getClientEnvVariables } from '@/_utils/getEnvVariables.client';

const { NEXT_PUBLIC_POSTHOG_KEY, NEXT_PUBLIC_POSTHOG_HOST } = getClientEnvVariables();

// Initialize PostHog once, deferred to avoid blocking hydration
let posthogInitialized = false;
function ensurePostHogInit() {
	if (posthogInitialized || typeof window === 'undefined' || !NEXT_PUBLIC_POSTHOG_KEY || !NEXT_PUBLIC_POSTHOG_HOST)
		return;
	posthogInitialized = true;
	posthog.init(NEXT_PUBLIC_POSTHOG_KEY, {
		api_host: NEXT_PUBLIC_POSTHOG_HOST,
		capture_pageview: true,
		capture_pageleave: true,
		autocapture: true,
		persistence: 'localStorage+cookie',
		person_profiles: 'identified_only'
	});
}

/**
 * Identifies the user in PostHog when session is available.
 * Resets on logout.
 */
function PostHogUserIdentifier() {
	const ph = usePostHog();
	const { data: session } = useSession();

	useEffect(() => {
		if (session?.user) {
			ph.identify(session.user.id, {
				email: session.user.email,
				name: session.user.name,
				beta_cohort: true
			});
		} else {
			ph.reset();
		}
	}, [ph, session?.user]);

	return null;
}

export default function PostHogProvider({ children }: { children: ReactNode }) {
	// If no key configured, render children without PostHog wrapper
	if (!NEXT_PUBLIC_POSTHOG_KEY) {
		return <>{children}</>;
	}

	// Defer PostHog init to after hydration to avoid blocking LCP
	ensurePostHogInit();

	return (
		<PHProvider client={posthog}>
			<PostHogUserIdentifier />
			{children}
		</PHProvider>
	);
}
